import {
  Button,
  Checkbox,
  Input,
  Loader,
  Text,
  Tooltip,
  useBrandingAsset,
  useLoginPlaceholder,
  useNetworkName,
  useTheme,
  useTranslation,
  zero,
} from "@streamplace/components";
import { Image } from "expo-image";
import useActorTypeahead from "hooks/useActorTypeahead";
import {
  ArrowRightToLine,
  AtSign,
  CornerDownLeft,
  Info,
} from "lucide-react-native";
import { useEffect, useMemo, useState } from "react";
import { Alert, Linking, Platform, Pressable, View } from "react-native";
import { useStore } from "store";
import { useLogin } from "store/hooks";
import PdsLoginForm from "./pds-login-form";

interface LoginFormProps {
  onSuccess?: () => void;
  onCloseModal?: () => void;
  onOpenPdsModal?: () => void;
  /** Show the node's OAuth flow even when branding picks the PDS login. */
  forceOAuth?: boolean;
}

// Branding key loginMode picks the sign-in: the node's OAuth flow, or the
// PDS's own email / app password + QR login. Either form links to the
// other, since only an OAuth session is attributable by the node (access
// roles, admin, streaming).
export default function LoginForm(props: LoginFormProps) {
  const mode = useBrandingAsset("loginMode")?.data;
  const [useOAuth, setUseOAuth] = useState(false);
  const pdsMode = mode === "pds";
  if (pdsMode && !useOAuth && !props.forceOAuth) {
    return (
      <PdsLoginForm
        onSuccess={props.onSuccess}
        inModal={!!props.onCloseModal}
        onUseOAuth={() => setUseOAuth(true)}
      />
    );
  }
  return (
    <OAuthLoginForm
      {...props}
      onUsePassword={
        pdsMode && !props.forceOAuth ? () => setUseOAuth(false) : undefined
      }
    />
  );
}

function OAuthLoginForm({
  onSuccess,
  onCloseModal,
  onOpenPdsModal,
  onUsePassword,
}: LoginFormProps & { onUsePassword?: () => void }) {
  const { theme } = useTheme();
  const { t } = useTranslation("common");
  const network = useNetworkName();
  const loginPlaceholder = useLoginPlaceholder();
  const loginAction = useStore((state) => state.login);
  const openLoginLink = useStore((state) => state.openLoginLink);
  const authStatus = useStore((state) => state.authStatus);
  const sessionKind = useStore((state) => state.sessionKind);
  const loginState = useLogin();
  const [handle, setHandle] = useState("");
  const [imageLoading, setImageLoading] = useState(false);
  const [blueskyPermissions, setBlueskyPermissions] = useState(true);
  const { actors } = useActorTypeahead(handle);

  const filteredActors = useMemo(
    () => actors.filter((actor) => actor.handle.startsWith(handle)),
    [actors, handle],
  );

  const suggestion = useMemo(
    () =>
      filteredActors.length > 0 &&
      handle.length >= 3 &&
      filteredActors[0].handle.startsWith(handle)
        ? filteredActors[0]
        : null,
    [filteredActors],
  );

  const completionText = useMemo(
    () =>
      suggestion && suggestion.handle
        ? suggestion.handle.slice(handle.length)
        : null,
    [suggestion, handle],
  );

  const avatarUri = useMemo(() => suggestion?.avatar, [suggestion?.avatar]);

  const submit = () => {
    if (completionText && isMobile) {
      acceptSuggestion();
      return;
    }
    let clean = handle;
    if (handle.startsWith("@")) clean = handle.slice(1);
    loginAction(clean, openLoginLink, { blueskyPermissions });
  };

  const acceptSuggestion = () => {
    if (suggestion) {
      setHandle(suggestion.handle);
    }
  };

  const onSignup = () => {
    onCloseModal?.();
    onOpenPdsModal?.();
  };

  const isMobile = Platform.OS === "ios" || Platform.OS === "android";

  const onKeyPress = (e: any) => {
    if (e.nativeEvent.key === "Enter") {
      submit();
    } else if (e.nativeEvent.key === "Tab" && completionText) {
      e.preventDefault();
      acceptSuggestion();
    } else if (e.nativeEvent.key === "ArrowRight" && completionText) {
      const input = e.target;
      if (input.selectionStart === handle.length) {
        e.preventDefault();
        acceptSuggestion();
      }
    } else if (e.nativeEvent.key === " " && completionText) {
      e.preventDefault();
      acceptSuggestion();
    }
  };

  useEffect(() => {
    if (loginState?.error) {
      Alert.alert("Login error", loginState.error);
    }
  }, [loginState?.error]);

  // Only an OAuth session counts as done here: a brokered or password
  // session is what the user is upgrading from, so it must not close the form.
  useEffect(() => {
    if (authStatus === "loggedIn" && sessionKind === "oauth" && onSuccess) {
      onSuccess();
    }
  }, [authStatus, sessionKind, onSuccess]);

  return (
    <>
      <View
        style={[
          zero.layout.flex.row,
          { flexWrap: "wrap" },
          zero.gap.all[1],
          zero.mb[4],
        ]}
      >
        <Text style={[{ color: theme.colors.textMuted }]}>
          Sign in using your handle on the AT Protocol
        </Text>
        <Pressable
          onPress={() => {
            const u = new URL("https://atmosphereaccount.com/");
            Linking.openURL(u.toString());
          }}
        >
          <Info size={16} style={{ paddingTop: 4 }} color={theme.colors.ring} />
        </Pressable>
        <Text style={[{ color: theme.colors.textMuted }]}>
          (e.g. your {network} handle)
        </Text>
      </View>

      <View style={[zero.mb[4], { position: "relative" }]}>
        <Text style={[{ color: theme.colors.text3, marginBottom: 8 }]}>
          Handle
        </Text>
        <View style={{ position: "relative" }}>
          {completionText && suggestion?.handle !== handle ? (
            <View
              style={[
                {
                  position: "absolute",
                  left: 13 + 28 + 6,
                  top: isMobile ? 15.25 : 12,
                  zIndex: 1000000,
                  pointerEvents: "none",
                },
                zero.layout.flex.row,
                zero.layout.flex.align.center,
                zero.gap.all[1],
              ]}
            >
              <Text
                style={[
                  {
                    color: theme.colors.text4,
                    pointerEvents: "none",
                    zIndex: 1000000,
                    fontSize: 16,
                  },
                ]}
              >
                <Text
                  style={{
                    opacity: 0.2,
                    fontSize: 16,
                  }}
                >
                  {handle}
                </Text>
                {completionText}
              </Text>
              {isMobile ? (
                <CornerDownLeft
                  height={18}
                  color={theme.colors.text4}
                  style={{
                    bottom: 2,
                  }}
                />
              ) : (
                <ArrowRightToLine
                  height={18}
                  color={theme.colors.text4}
                  style={{
                    paddingBottom: 1,
                  }}
                />
              )}
            </View>
          ) : (
            <></>
          )}
          <View
            style={[
              zero.layout.position.absolute,
              zero.layout.flex.row,
              { zIndex: 32, top: 8, left: 10 },
            ]}
          >
            {avatarUri ? (
              <View
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 900,
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {imageLoading && (
                  <View
                    style={{
                      position: "absolute",
                      zIndex: 1,
                    }}
                  >
                    <Loader />
                  </View>
                )}
                <Image
                  key={avatarUri}
                  source={{ uri: avatarUri }}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 999,
                    opacity: suggestion?.handle === handle ? 1 : 0.7,
                  }}
                  onLayout={() => setImageLoading(true)}
                  onLoad={() => setImageLoading(false)}
                  onError={() => setImageLoading(false)}
                />
              </View>
            ) : (
              <View
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 900,
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <AtSign size={20} color={theme.colors.text2} />
              </View>
            )}
          </View>
          <Input
            value={handle}
            onChangeText={(text) =>
              setHandle(
                text
                  .toLowerCase()
                  .replace(/[\u202A\u202C\u200E\u200F\u2066-\u2069]/g, "")
                  .trim(),
              )
            }
            placeholder={loginPlaceholder ?? "jcsalterego.bsky.social"}
            onKeyPress={onKeyPress}
            onSubmitEditing={submit}
            autoCapitalize="none"
            autoCorrect={false}
            keyboardType="url"
            placeholderTextColor={theme.colors.textMuted}
            containerStyle={{
              paddingLeft: 46,
            }}
          />
        </View>
      </View>

      <View
        style={[
          zero.layout.flex.row,
          zero.gap.all[3],
          zero.mb[4],
          { alignItems: "center" },
        ]}
      >
        {/* preview of the Bluesky LIVE ring the user gets while streaming */}
        <View style={{ alignItems: "center", width: 56 }}>
          <View
            style={{
              width: 48,
              height: 48,
              borderRadius: 999,
              borderWidth: 2.5,
              borderColor: blueskyPermissions ? "red" : theme.colors.border,
              padding: 2,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {avatarUri ? (
              <Image
                source={{ uri: avatarUri }}
                style={{ width: "100%", height: "100%", borderRadius: 999 }}
              />
            ) : (
              <View
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: 999,
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: theme.colors.muted,
                }}
              >
                <AtSign size={22} color={theme.colors.text2} />
              </View>
            )}
          </View>
          <View
            style={{
              marginTop: -8,
              backgroundColor: "red",
              borderRadius: 4,
              paddingHorizontal: 4,
              paddingVertical: 1,
              opacity: blueskyPermissions ? 1 : 0,
            }}
          >
            <Text
              style={{
                color: "white",
                fontSize: 9,
                fontWeight: "700",
                letterSpacing: 0.5,
              }}
            >
              LIVE
            </Text>
          </View>
        </View>
        <Tooltip
          content={t("login-show-live-on-bluesky-description", { network })}
          position="top"
          style={{ flex: 1 }}
        >
          <Checkbox
            checked={blueskyPermissions}
            onCheckedChange={setBlueskyPermissions}
            label={t("login-show-live-on-bluesky", { network })}
          />
        </Tooltip>
      </View>

      <View
        style={[
          zero.layout.flex.row,
          { justifyContent: "flex-end", zIndex: -32 },
          zero.gap.all[3],
        ]}
      >
        <Button width="min" onPress={() => onSignup()} variant="ghost">
          Sign Up
        </Button>
        <Button
          onPress={submit}
          disabled={loginState.loading}
          style={[zero.px[6]]}
          width="min"
          loading={loginState.loading}
        >
          Log In
        </Button>
      </View>
      {onUsePassword ? (
        <Pressable onPress={onUsePassword} style={[zero.mt[4]]}>
          <Text style={{ color: theme.colors.text3, fontSize: 13 }}>
            Sign in with email and password instead
          </Text>
        </Pressable>
      ) : null}
    </>
  );
}
